import React, { useState, useEffect } from "react";
import dayjs, { Dayjs } from "dayjs";
import { Calendar } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

interface Preset {
  label: string;
  value: [Dayjs, Dayjs];
}

interface DateRangePickerProps {
  value?: [Dayjs | null, Dayjs | null] | null;
  onChange?: (value: [Dayjs, Dayjs] | null) => void;
  presets?: Preset[];
  format?: string;
  className?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  value,
  onChange,
  presets = [],
  format = "MMM DD, YYYY",
  className,
  style,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  // Sync internal state with value prop
  useEffect(() => {
    if (value?.[0]) {
      setStartDate(value[0].format("YYYY-MM-DD"));
    }
    if (value?.[1]) {
      setEndDate(value[1].format("YYYY-MM-DD"));
    }
  }, [value]);

  const formatDisplay = (date: Dayjs | null | undefined): string => {
    if (!date) return "";
    return date.format(format);
  };

  const handlePresetClick = (preset: Preset) => {
    onChange?.(preset.value);
    setOpen(false);
  };

  const handleApply = () => {
    if (startDate && endDate) {
      const start = dayjs(startDate);
      const end = dayjs(endDate);
      onChange?.([start, end]);
    }
    setOpen(false);
  };

  const displayValue = value?.[0] && value?.[1]
    ? `${formatDisplay(value[0])} - ${formatDisplay(value[1])}`
    : "Select date range";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          style={style}
          className={cn(
            "justify-start text-left font-normal",
            !value && "text-muted-foreground",
            className
          )}
        >
          <Calendar className="mr-2 h-4 w-4" />
          <span>{displayValue}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0" align="start">
        <div className="flex">
          {/* Presets sidebar */}
          {presets.length > 0 && (
            <div className="border-r p-2 space-y-1">
              {presets.map((preset, index) => (
                <Button
                  key={index}
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start text-sm"
                  onClick={() => handlePresetClick(preset)}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          )}

          {/* Date inputs */}
          <div className="p-3 space-y-3">
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">From</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="flex h-8 w-[140px] rounded-md border border-input bg-transparent px-2 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">To</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="flex h-8 w-[140px] rounded-md border border-input bg-transparent px-2 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleApply}>
                Apply
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
